/**
 * The customer-classification badge, shown next to a customer's name everywhere
 * staff see a customer — the detail header, the admin list, the salesman's beat,
 * the driver's stops, the map, the ledger.
 *
 * Colours are the five tiers from the classification sheet: Trusted green, Cash
 * only blue, Watch amber, High risk light red, Blacklisted a deep red with a
 * no-entry mark. A blacklisted (and, less loudly, a high-risk) customer is a
 * selling decision a driver or salesman must not get wrong, so the name itself
 * is reddened for those two — `classificationNameStyle` returns that style — and
 * a blacklisted name is struck through. Every other tier leaves the name black.
 *
 * One component so the whole app reads the same and cannot drift tier by tier.
 */

import type { CSSProperties } from 'react';
import { IconBan } from './Icons';

interface Tier {
  label: string;
  dot: string;    // the status dot / icon colour
  text: string;   // badge text colour (dark end of the ramp)
  bg: string;     // badge pill background (light end of the ramp)
  /** Risk tiers also colour the NAME; blacklisted strikes it and shows the ban. */
  nameColor?: string;
  ban?: boolean;
  strike?: boolean;
}

const TIERS: Record<string, Tier> = {
  // nameColor is a legible shade of the tier colour (the bright dot colour is too
  // light for text on white — amber especially). The customer name is shown in it
  // everywhere, so the name itself carries the classification.
  trusted:     { label: 'Trusted',     dot: '#639922', text: '#27500a', bg: '#eaf3de', nameColor: '#4e7a1a' },
  cash_only:   { label: 'Cash only',   dot: '#378ADD', text: '#0c447c', bg: '#e6f1fb', nameColor: '#1c5fa0' },
  watch:       { label: 'Watch',       dot: '#EF9F27', text: '#633806', bg: '#faeeda', nameColor: '#a5720d' },
  high_risk:   { label: 'High risk',   dot: '#D85A30', text: '#712b13', bg: '#faece7', nameColor: '#c0392b' },
  blacklisted: { label: 'Blacklisted', dot: '#7a0f0f', text: '#5a0f0f', bg: '#fcebeb', nameColor: '#7a0f0f', ban: true, strike: true },
};

export function tierOf(classification?: string | null): Tier {
  return TIERS[classification ?? 'trusted'] ?? TIERS.trusted!;
}

/** The style to apply to a customer's NAME: reddened for risk, struck for blacklist. */
export function classificationNameStyle(classification?: string | null): CSSProperties {
  const t = tierOf(classification);
  if (!t.nameColor) return {};
  return { color: t.nameColor, ...(t.strike ? { textDecoration: 'line-through' } : {}) };
}

export function ClassificationBadge({ classification, className }: {
  classification?: string | null;
  className?: string;
}) {
  const t = tierOf(classification);
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: t.bg, color: t.text,
        borderRadius: 999, padding: '2px 10px',
        fontSize: 'var(--fs-xs)', fontWeight: 600, whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {t.ban
        ? <IconBan width={13} height={13} style={{ color: t.dot }} />
        : <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.dot, flex: '0 0 auto' }} />}
      {t.label}
    </span>
  );
}
